import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, Check, ExternalLink, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Italian province code → name (for tooltip in copy panel)
const PROV_NAMES = {
  AG: "Agrigento", AL: "Alessandria", AN: "Ancona", AO: "Aosta", AR: "Arezzo",
  AP: "Ascoli Piceno", AT: "Asti", AV: "Avellino", BA: "Bari", BT: "Barletta-Andria-Trani",
  BL: "Belluno", BN: "Benevento", BG: "Bergamo", BI: "Biella", BO: "Bologna",
  BZ: "Bolzano", BS: "Brescia", BR: "Brindisi", CA: "Cagliari", CL: "Caltanissetta",
  CB: "Campobasso", CE: "Caserta", CT: "Catania", CZ: "Catanzaro", CH: "Chieti",
  CO: "Como", CS: "Cosenza", CR: "Cremona", KR: "Crotone", CN: "Cuneo",
  EN: "Enna", FM: "Fermo", FE: "Ferrara", FI: "Firenze", FG: "Foggia",
  FC: "Forlì-Cesena", FR: "Frosinone", GE: "Genova", GO: "Gorizia", GR: "Grosseto",
  IM: "Imperia", IS: "Isernia", AQ: "L'Aquila", SP: "La Spezia", LT: "Latina",
  LE: "Lecce", LC: "Lecco", LI: "Livorno", LO: "Lodi", LU: "Lucca",
  MC: "Macerata", MN: "Mantova", MS: "Massa-Carrara", MT: "Matera", ME: "Messina",
  MI: "Milano", MO: "Modena", MB: "Monza e Brianza", NA: "Napoli", NO: "Novara",
  NU: "Nuoro", OR: "Oristano", PD: "Padova", PA: "Palermo", PR: "Parma",
  PV: "Pavia", PG: "Perugia", PU: "Pesaro-Urbino", PE: "Pescara", PC: "Piacenza",
  PI: "Pisa", PT: "Pistoia", PN: "Pordenone", PZ: "Potenza", PO: "Prato",
  RG: "Ragusa", RA: "Ravenna", RC: "Reggio Calabria", RE: "Reggio Emilia", RI: "Rieti",
  RN: "Rimini", RM: "Roma", RO: "Rovigo", SA: "Salerno", SS: "Sassari",
  SV: "Savona", SI: "Siena", SR: "Siracusa", SO: "Sondrio", SU: "Sud Sardegna",
  TA: "Taranto", TE: "Teramo", TR: "Terni", TO: "Torino", TP: "Trapani",
  TN: "Trento", TV: "Treviso", TS: "Trieste", UD: "Udine", VA: "Varese",
  VE: "Venezia", VB: "Verbano-Cusio-Ossola", VC: "Vercelli", VR: "Verona", VV: "Vibo Valentia",
  VI: "Vicenza", VT: "Viterbo",
};

// Convert yyyy-mm-dd → dd/mm/yyyy
function fmtDateIt(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

function CopyRow({ label, value, hint, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`Copiato: ${label}`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Impossibile copiare");
    }
  };
  return (
    <div className="flex items-center gap-2 py-2 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
          {label}
        </div>
        <div className="text-sm font-medium text-gray-900 truncate" title={String(value)}>
          {value}
        </div>
        {hint && <div className="text-[10px] text-gray-400 italic">{hint}</div>}
      </div>
      <button
        onClick={onCopy}
        data-testid={testid}
        className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
          copied
            ? "bg-emerald-500 text-white"
            : "bg-teal-600 hover:bg-teal-700 text-white"
        }`}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Copiato" : "Copia"}
      </button>
    </div>
  );
}

export default function CsenFillPage() {
  const [searchParams] = useSearchParams();

  const data = useMemo(() => {
    const get = (k) => searchParams.get(k) || "";
    return {
      surname: get("surname"),
      name: get("name"),
      sex: get("sex"),
      birth_date: get("birth_date"),
      birth_place: get("birth_place"),
      birth_province: get("birth_province"),
      fiscal_code: get("fiscal_code"),
      email: get("email"),
      phone: get("phone"),
      nationality: get("nationality"),
      is_foreign: get("is_foreign") === "1",
    };
  }, [searchParams]);

  const [proxyUrl, setProxyUrl] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("ppsc_admin_token");
    if (!token) {
      window.location.href = "/admin";
      return;
    }
    if (data.is_foreign) {
      // Foreigners → proxy with auto-fill (no CONTROLLA C.F. AJAX needed)
      const params = new URLSearchParams({
        token,
        surname: data.surname,
        name: data.name,
        sex: data.sex,
        birth_date: data.birth_date,
        email: data.email,
        phone: data.phone,
        nationality: data.nationality,
        is_foreign: "1",
      });
      setProxyUrl(`${BACKEND_URL}/api/admin/csen-fill?${params.toString()}`);
    } else {
      // Italians → real CSEN site (cookies/AJAX needed for CONTROLLA C.F.)
      setProxyUrl("https://server3.conceptstudio.it/website/csensassari/iscrizioni/?q=lpd3GpcD95");
    }
  }, [data]);

  const phoneDigits = (data.phone || "").replace(/\D/g, "");
  const provinceLabel = data.birth_province
    ? `${data.birth_province}${PROV_NAMES[data.birth_province] ? " — " + PROV_NAMES[data.birth_province] : ""}`
    : "";

  // Detect mobile (< 1024px). On mobile we hide the iframe and offer a "Open CSEN"
  // button — split-view is unusable on small screens.
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 1024);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-50">
      {/* Top bar */}
      <header className="shrink-0 bg-white border-b border-gray-200 px-3 sm:px-4 py-2.5 flex items-center justify-between gap-2 shadow-sm">
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = "/admin")}
          data-testid="csen-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-teal-700 font-medium shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Torna alla Dashboard</span>
          <span className="sm:hidden">Indietro</span>
        </button>
        <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate text-center flex-1 min-w-0">
          CSEN — {data.surname} {data.name}
          {data.is_foreign && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] sm:text-xs">
              Straniero
            </span>
          )}
        </div>
        <a
          href={proxyUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="open-csen-newtab"
          className="inline-flex items-center gap-1.5 text-sm text-white bg-teal-600 hover:bg-teal-700 px-3 py-1.5 rounded-md font-semibold shrink-0"
        >
          <span className="hidden sm:inline">Apri in nuova scheda</span>
          <span className="sm:hidden">Apri CSEN</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </header>

      {/* Split view (desktop) / Stacked (mobile) */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left — CSEN form in iframe (hidden on mobile) */}
        {!isMobile && (
          <div className="flex-1 bg-white border-r border-gray-200 overflow-hidden">
            {proxyUrl && (
              <iframe
                src={proxyUrl}
                title="Modulo CSEN"
                data-testid="csen-iframe"
                className="w-full h-full border-0"
              />
            )}
          </div>
        )}

        {/* Right — copy panel */}
        <aside className={`${isMobile ? "w-full" : "w-[380px]"} shrink-0 bg-white overflow-y-auto p-4 shadow-inner`}>
          <div className="mb-3">
            <h2 className="text-base font-bold text-gray-900">Dati da copiare</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isMobile ? (
                <>Tocca <strong>Apri CSEN</strong> in alto per aprire il modulo in una nuova scheda, poi torna qui per <strong>copiare</strong> i campi e incollarli.</>
              ) : (
                <>Clicca <strong>Copia</strong> su un campo, poi <strong>incolla</strong> nel modulo CSEN a sinistra.</>
              )}
            </p>
          </div>

          {/* Instructions */}
          <div className="mb-4 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900 leading-relaxed">
            {data.is_foreign ? (
              <>
                <strong>Straniero — Auto-compilazione attiva.</strong> Il modulo CSEN viene compilato automaticamente con tutti i dati. Verifica i campi e clicca <em>INSERISCI</em>. I bottoni Copia qui sotto sono di backup.
              </>
            ) : (
              <>
                <strong>Italiano — Modalità copia-incolla.</strong> Compila i campi dal pannello a destra usando i bottoni <em>Copia</em>, poi seleziona Provincia e Città dai menu CSEN. Infine clicca <em>CALCOLA IL CODICE FISCALE</em>.
              </>
            )}
          </div>

          <div className="space-y-0">
            <CopyRow label="Cognome" value={(data.surname || "").toUpperCase()} testid="copy-surname" />
            <CopyRow label="Nome" value={(data.name || "").toUpperCase()} testid="copy-name" />
            {data.sex && <CopyRow label="Sesso" value={data.sex} testid="copy-sex" />}
            {data.birth_date && (
              <CopyRow
                label="Data di Nascita"
                value={fmtDateIt(data.birth_date)}
                hint={`(formato CSEN: gg/mm/aaaa — valore date input: ${data.birth_date})`}
                testid="copy-birth-date"
              />
            )}
            {!data.is_foreign && data.birth_province && (
              <CopyRow
                label="Provincia di Nascita"
                value={provinceLabel}
                hint="Selezionala dal menù 'Provincia' nel CSEN"
                testid="copy-birth-province"
              />
            )}
            {!data.is_foreign && data.birth_place && (
              <CopyRow
                label="Città di Nascita"
                value={data.birth_place}
                hint="Selezionala dal menù 'Città' nel CSEN (appare dopo aver scelto la Provincia)"
                testid="copy-birth-place"
              />
            )}
            {data.is_foreign && data.nationality && (
              <CopyRow
                label="Nazionalità"
                value={data.nationality}
                hint="Selezionala dal menù dopo aver cliccato '[ straniero senza codice fiscale ]'"
                testid="copy-nationality"
              />
            )}
            {data.fiscal_code && (
              <CopyRow
                label="Codice Fiscale"
                value={data.fiscal_code.toUpperCase()}
                hint="Solo se preferisci incollarlo invece di calcolarlo dal CSEN"
                testid="copy-fiscal-code"
              />
            )}
            <CopyRow label="Email" value={data.email} testid="copy-email" />
            {phoneDigits && (
              <CopyRow
                label="Telefono (solo cifre)"
                value={phoneDigits}
                hint={data.phone !== phoneDigits ? `Originale: ${data.phone}` : null}
                testid="copy-phone"
              />
            )}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-200 text-[11px] text-gray-500 leading-relaxed">
            Suggerimento: usa <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px]">Tab</kbd> nel modulo CSEN per spostarti rapidamente tra i campi.
          </div>
        </aside>
      </div>
    </div>
  );
}
